import moment from 'moment'
import { useContext } from 'react'
import { useIntl } from 'react-intl'

import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import GeoContext from 'contexts/geoContext'
import { isOneWayFlight } from 'lib/flights/flightUtils'
import { formatDynamicAmountWithDollarType } from 'lib/format/formatCurrencyIntl'
import { extractOfferIdsFromOrder } from 'tripPlanner/components/EntryPoints/utils'
import BookingItemCard from 'tripPlanner/components/ItemCard/BookingItemCard'
import { useFlightDetailsForItem } from 'tripPlanner/hooks/api/flights'
import type { OfferTypeForTripItemTypes } from 'tripPlanner/hooks/api/common'
import { concatStringWithDot } from 'tripPlanner/utils'
import useOffers from 'hooks/Offers/useOffers'

type Props = {
  orderFlightItem: App.OrderFlightItem
  showBorder?: boolean
  order: App.Order
  onSelect?: (
    order: App.Order,
    offers: Array<OfferTypeForTripItemTypes>,
    checked: boolean,
  ) => void
}

function FlightBookingCard(props: Props) {
  const intl = useIntl()
  const { currentCurrency, currentRegionCode } = useContext(GeoContext)
  const { orderFlightItem, showBorder, order, onSelect } = props
  const { orderId, id } = orderFlightItem

  const [offers, isOffersLoading] = useOffers(extractOfferIdsFromOrder(order))
  const { data: flightDetails, isLoading: isFlightDetailsLoading } =
    useFlightDetailsForItem({ orderId, itemId: id })

  const { fares, tripType } = flightDetails?.journeyFares || {}

  const [departing, returning] = fares ?? []

  const departingFlight = departing?.flightGroup.flights[0]
  const isOneWayFare = isOneWayFlight(tripType ?? '')

  const arrivalFlight = departing?.flightGroup.flights[departing?.flightGroup.flights.length - 1]

  const arrivalFlightLabel = (() => {
    const airportName = isOneWayFare ? arrivalFlight?.arrivalAirportName : arrivalFlight?.departureAirportName
    const country = isOneWayFare ? arrivalFlight?.arrivalCountry : arrivalFlight?.departureCountry
    if (airportName && country) {
      return intl.formatMessage(
        {
          id: 'QoP-BsCT6AmUGe1rEx1m',
          defaultMessage: '{airportName}, {country}',
          description: 'Flight booking card airport name and country separated by comma',
        },
        { airportName, country },
      )
    }
    return airportName || country || ''
  })()

  const isFetching = isOffersLoading || isFlightDetailsLoading

  const renderStartAndEndDates = () => {
    let startAndEndDates = ''
    if (departing?.flightGroup.flights[0].departingDate) {
      startAndEndDates += moment(
        departing?.flightGroup.flights[0].departingDate,
      ).format('DD MMM')
    }

    if (returning?.flightGroup.flights[0].arrivalDate) {
      startAndEndDates += ` - ${moment(
        returning.flightGroup.flights[0].arrivalDate,
      ).format('DD MMM YYYY')}`
    }

    return startAndEndDates
  }

  if (isFetching || !departingFlight || !arrivalFlight) {
    return <LoadingIndicator inline />
  }

  return (
    <BookingItemCard
      heading={intl.formatMessage(
        {
          id: '9E-_NPahr6Wx8GWGzZOI',
          defaultMessage: '{departureAirportName}, {departureCountry} to {arrivalFlightLabel}',
          description: 'Flight booking card heading showing departure city and country to arrival destination',
        },
        {
          departureAirportName: departingFlight.departureAirportName,
          departureCountry: departingFlight.departureCountry,
          arrivalFlightLabel,
        },
      )}
      subHeading={concatStringWithDot(
        renderStartAndEndDates(),
        formatDynamicAmountWithDollarType(
          orderFlightItem.total,
          currentCurrency,
          currentRegionCode,
        ),
      )}
      showBorder={showBorder}
      imgUrl={departing?.carrierLogo}
      onSelect={(checked) => {
        if (onSelect) {
          onSelect(order, offers, checked)
        }
      }}
    />
  )
}

export default FlightBookingCard
